/**
 * Zentrale Preiskonfiguration für SkyVision LINEAR
 */

// Grundkonfiguration für SkyVision LINEAR
export const SKYVISION_LINEAR_CONFIG = {
  basePrice: 2699, // Grundpreis in Euro
  baseArea: 1, // Standard-Fläche in m²
  
  // Preisstufen basierend auf Fläche
  get priceSteps() {
    return [
      { maxArea: 1, price: this.basePrice },        // Bis 1m²: Grundpreis
      { maxArea: 3, price: this.basePrice + 400 },  // Bis 3m²: +400€
      { maxArea: 6, price: this.basePrice + 800 },  // Bis 6m²: +800€
      { maxArea: 10, price: this.basePrice + 1200 } // Bis 10m²: +1200€
    ];
  },
  
  // Modulare Eigenschaften
  moduleConfig: {
    minModules: 1,
    maxModules: 10,
    standardModuleSize: { width: 1000, length: 1000 }, // in mm
    pricePerModule: 200 // Zusätzlicher Preis pro Modul (falls modularer Ansatz gewünscht)
  },
  
  // Erweiterte Preiskonfiguration für zukünftige Flexibilität
  priceRules: {
    // Aktiviert flächenbasierte Preise
    areaBasedPricing: true,
    
    // Optional: Modulbasierte Preise (alternativ zu flächenbasiert)
    moduleBasedPricing: false,
    
    // Optional: Staffelpreise für größere Projekte
    bulkDiscounts: {
      enabled: false,
      thresholds: [
        { minArea: 20, discountPercent: 5 },   // 5% Rabatt ab 20m²
        { minArea: 50, discountPercent: 10 }   // 10% Rabatt ab 50m²
      ]
    },
    
    // Optional: Flächenlimits
    limits: {
      minArea: 0.5,   // Mindestfläche in m²
      maxArea: 15     // Maximale Fläche in m²
    }
  },
  
  glassTypes: {
    double: {
      name: '2-fach Isolierverglasung',
      description: 'Sehr gute Wärmedämmung (Ug-Wert 1,1)',
      priceModifier: 0 // Kein Aufpreis für 2-fach
    },
    triple: {
      name: '3-fach Isolierverglasung', 
      description: 'Maximale Energieeffizienz (Ug-Wert 0,7)',
      priceModifier: 800 // 800 Aufpreis für 3-fach
    }
  }
};

/**
 * Berechnet den Preis für SkyVision LINEAR basierend auf der Fläche
 */
export const calculateLinearPrice = (
  area: number,
  glassType: 'double' | 'triple' = 'double'
): number => {
  const config = SKYVISION_LINEAR_CONFIG;
  
  // Finde passende Preisstufe basierend auf Fläche
  const priceStep = config.priceSteps.find(step => area <= step.maxArea) || 
                   config.priceSteps[config.priceSteps.length - 1];
  
  let totalPrice = priceStep.price;
  
  // Aufpreis für Verglasung
  if (glassType && config.glassTypes[glassType]) {
    totalPrice += config.glassTypes[glassType].priceModifier;
  }
  
  // Optional: Staffelrabatte anwenden
  if (config.priceRules.bulkDiscounts.enabled) {
    const applicableDiscount = config.priceRules.bulkDiscounts.thresholds
      .filter(threshold => area >= threshold.minArea)
      .sort((a, b) => b.discountPercent - a.discountPercent)[0];
    
    if (applicableDiscount) {
      totalPrice = totalPrice * (1 - applicableDiscount.discountPercent / 100);
    }
  }
  
  return Math.round(totalPrice);
};

/**
 * Berechnet den Preis basierend auf Modulanzahl (alternative Berechnung)
 */
export const calculateLinearPriceByModules = (
  moduleCount: number,
  glassType: 'double' | 'triple' = 'double'
): number => {
  const config = SKYVISION_LINEAR_CONFIG;
  
  if (!config.priceRules.moduleBasedPricing) {
    // Fallback: Berechne Fläche und verwende normale Preisberechnung
    const area = moduleCount * 1; // 1m² pro Modul (angenommen)
    return calculateLinearPrice(area, glassType);
  }
  
  let totalPrice = config.basePrice + (moduleCount - 1) * config.moduleConfig.pricePerModule;
  
  // Aufpreis für Verglasung
  if (glassType && config.glassTypes[glassType]) {
    totalPrice += config.glassTypes[glassType].priceModifier;
  }
  
  return Math.round(totalPrice);
};

/**
 * Prüft ob die Fläche gültig ist
 */
export const validateLinearDimensions = (
  area: number
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const config = SKYVISION_LINEAR_CONFIG;
  
  if (area < config.priceRules.limits.minArea) {
    errors.push(`Mindestfläche: ${config.priceRules.limits.minArea}m²`);
  }
  
  if (area > config.priceRules.limits.maxArea) {
    errors.push(`Maximale Fläche: ${config.priceRules.limits.maxArea}m²`);
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Gibt eine detaillierte Preisaufschlüsselung zurück
 */
export const getLinearPriceBreakdown = (
  area: number,
  glassType: 'double' | 'triple' = 'double'
): {
  basePrice: number;
  priceStep: string;
  glassSurcharge: number;
  bulkDiscount: number;
  totalPrice: number;
  breakdown: string[];
} => {
  const config = SKYVISION_LINEAR_CONFIG;
  
  const priceStep = config.priceSteps.find(step => area <= step.maxArea) || 
                   config.priceSteps[config.priceSteps.length - 1];
  
  const glassSurcharge = glassType && config.glassTypes[glassType] ? 
    config.glassTypes[glassType].priceModifier : 0;

  let totalPrice = priceStep.price + glassSurcharge;
  let bulkDiscount = 0;
  
  // Berechne Staffelrabatt
  if (config.priceRules.bulkDiscounts.enabled) {
    const applicableDiscount = config.priceRules.bulkDiscounts.thresholds
      .filter(threshold => area >= threshold.minArea)
      .sort((a, b) => b.discountPercent - a.discountPercent)[0];
    
    if (applicableDiscount) {
      bulkDiscount = totalPrice * (applicableDiscount.discountPercent / 100);
      totalPrice = totalPrice - bulkDiscount;
    }
  }
  
  const stepDescription = area <= config.baseArea ? 
    'Grundpreis' : 
    `Fläche bis ${priceStep.maxArea}m²`;

  const breakdown = [
    `${stepDescription}: ${priceStep.price}€`,
    ...(glassSurcharge > 0 ? [`Verglasung (${config.glassTypes[glassType].name}): +${glassSurcharge}€`] : []),
    ...(bulkDiscount > 0 ? [`Mengenrabatt: -${Math.round(bulkDiscount)}€`] : [])
  ];

  return {
    basePrice: priceStep.price,
    priceStep: stepDescription,
    glassSurcharge,
    bulkDiscount: Math.round(bulkDiscount),
    totalPrice: Math.round(totalPrice),
    breakdown
  };
};

/**
 * Hilfsfunktionen für erweiterte Preiskonfiguration
 */

/**
 * Aktualisiert den Grundpreis (automatische Neuberechnung der Preisstufen)
 */
export const updateLinearBasePrice = (newPrice: number): void => {
  SKYVISION_LINEAR_CONFIG.basePrice = newPrice;
  // Preisstufen werden automatisch über den getter neu berechnet
};

/**
 * Gibt die verfügbaren Flächen-Stufen zurück
 */
export const getLinearAreaSteps = (): number[] => {
  return SKYVISION_LINEAR_CONFIG.priceSteps.map(step => step.maxArea);
};

/**
 * Berechnet empfohlene Modulanzahl für eine gegebene Fläche
 */
export const calculateRecommendedModules = (area: number): number => {
  const config = SKYVISION_LINEAR_CONFIG;
  const moduleArea = (config.moduleConfig.standardModuleSize.width * config.moduleConfig.standardModuleSize.length) / 1000000; // in m²
  return Math.ceil(area / moduleArea);
};

/**
 * Schaltet zwischen flächen- und modulbasierter Preisberechnung um
 */
export const togglePricingMode = (useModulePricing: boolean): void => {
  SKYVISION_LINEAR_CONFIG.priceRules.areaBasedPricing = !useModulePricing;
  SKYVISION_LINEAR_CONFIG.priceRules.moduleBasedPricing = useModulePricing;
};

// Exportiere für Backward-Kompatibilität
export const LINEAR_BASE_PRICE = SKYVISION_LINEAR_CONFIG.basePrice;
export const getProductBasePrice = () => SKYVISION_LINEAR_CONFIG.basePrice;
export const getProductName = () => 'SkyVision LINEAR';
